import React from 'react';
import PropTypes from 'prop-types';

import ReusableButton from 'components/common/ReusableButton';
import ReusableSelect from 'components/common/ReusableSelect';
import ReusableSnackbar from 'components/common/ReusableSnackbar';
import ReusableTextField from 'components/common/ReusableTextField';
import history from 'wrappers/history';
import styles from './style.module.scss';
import { yearOptions } from '../AdminUtils.ts';

class EventManagementPage extends React.Component {
  constructor(props) {
    super(props);

    if (!this.props.isLoggedIn) {
      history.push('/login');
    } else if (!this.props.isAdmin) {
      history.push('/forbidden');
    }

    this.state = {
      motd: props.motd,
      emailSignature: props.emailSignature,
      yaspFormLink: props.yaspFormLink,
      isDirty: false,
      isSaving: false,
      requestedEventDateId: null,
    };
  }

  async componentDidMount() {
    const requests = [];

    if (this.props.eventDates.length === 0) {
      requests.push(this.props.getEventDates());
    }
    if (this.props.year === '') {
      requests.push(this.props.getYear());
    }

    if (requests.length > 0) {
      await Promise.all(requests);
    }

    this.loadEventAdminIfReady();
  }

  componentDidUpdate(prevProps) {
    const settingsChanged = prevProps.motd !== this.props.motd
      || prevProps.emailSignature !== this.props.emailSignature
      || prevProps.yaspFormLink !== this.props.yaspFormLink;

    this.loadEventAdminIfReady();

    if (settingsChanged && !this.state.isDirty) {
      this.syncStateWithProps();
    }
  }

  handleMOTDChange(event) {
    const motd = event.target.value;

    this.setState((previousState) => {
      const nextValues = {
        ...previousState,
        motd,
      };

      return {
        motd,
        isDirty: this.hasChanges(nextValues),
      };
    });
  }

  handleEmailSignatureChange(event) {
    const emailSignature = event.target.value;

    this.setState((previousState) => {
      const nextValues = {
        ...previousState,
        emailSignature,
      };

      return {
        emailSignature,
        isDirty: this.hasChanges(nextValues),
      };
    });
  }

  handleYaspFormLinkChange(event) {
    const yaspFormLink = event.target.value;

    this.setState((previousState) => {
      const nextValues = {
        ...previousState,
        yaspFormLink,
      };

      return {
        yaspFormLink,
        isDirty: this.hasChanges(nextValues),
      };
    });
  }

  handleYearChange(event) {
    if (String(event.target.value) === String(this.props.year)) {
      return;
    }

    const newYear = { 'year': event.target.value };
    this.syncStateWithProps();
    this.props.updateYear(newYear);
  }

  getCurrentEventDateId() {
    const currentYear = parseInt(this.props.year, 10);
    const currentEventDate = this.props.eventDates.find((eventDate) => (
      parseInt(eventDate.event_id, 10) === currentYear
    ));

    return currentEventDate ? currentEventDate.id : null;
  }

  syncStateWithProps() {
    this.setState({
      motd: this.props.motd,
      emailSignature: this.props.emailSignature,
      yaspFormLink: this.props.yaspFormLink,
      isDirty: false,
    });
  }

  loadEventAdminIfReady() {
    const currentEventDateId = this.getCurrentEventDateId();
    if (!currentEventDateId || currentEventDateId === this.state.requestedEventDateId) {
      return;
    }

    this.setState({ requestedEventDateId: currentEventDateId });
    this.props.getEventAdmin(currentEventDateId);
  }

  hasChanges(values) {
    return values.motd !== this.props.motd
      || values.emailSignature !== this.props.emailSignature
      || values.yaspFormLink !== this.props.yaspFormLink;
  }

  hasBlankField() {
    return this.state.motd.trim() === ''
      || this.state.emailSignature.trim() === ''
      || this.state.yaspFormLink.trim() === '';
  }

  isSaveDisabled() {
    return !this.getCurrentEventDateId()
      || !this.state.isDirty
      || this.hasBlankField()
      || this.state.isSaving;
  }

  saveCurrentValues() {
    const currentEventDateId = this.getCurrentEventDateId();
    if (!currentEventDateId) {
      return Promise.resolve();
    }

    this.setState({ isSaving: true });

    return this.props.updateEventAdmin(currentEventDateId, {
      motd: this.state.motd,
      yasp_form_link: this.state.yaspFormLink,
      email_signature: this.state.emailSignature,
    })
      .then(() => {
        this.setState({
          isDirty: false,
          isSaving: false,
        });
      })
      .catch(() => {
        this.setState({ isSaving: false });
      });
  }

  retryUpdate() {
    this.saveCurrentValues();
  }

  render() {
    return (
      <div id="maincontent">
        <div className="wdn-band">
          <div className="wdn-inner-wrapper">
            <div className={styles['page-header']}>
              <h2 className={styles['no-margin-top']}>
                Event Admin
              </h2>
              <div className={styles['event-date-select']}>
                <ReusableSelect
                  id="year"
                  label="Event Date"
                  className={styles['year-select']}
                  value={this.props.year}
                  options={yearOptions(this.props.eventDates)}
                  onChange={(event) => this.handleYearChange(event)}
                  excludeNoneOption
                />
              </div>
            </div>
            <hr className={styles['divider']} />
            <div className={styles['grid-container']}>
              <h5 className={styles['header-gray']}>
                Message of the Day
              </h5>
              <div className={styles['grid-item-gray']}>
                <ReusableTextField
                  id="motd"
                  label="MOTD"
                  multiline
                  value={this.state.motd}
                  onChange={(event) => this.handleMOTDChange(event)}
                />
              </div>

              <h5 className={styles['header-white']}>
                YASP Form Link
              </h5>
              <div className={styles['grid-item-white']}>
                <ReusableTextField
                  id="yaspFormLink"
                  label="YASP Form Link"
                  multiline
                  value={this.state.yaspFormLink}
                  onChange={(event) => this.handleYaspFormLinkChange(event)}
                />
              </div>

              <h5 className={styles['header-gray']}>
                Email Signature
              </h5>
              <div className={styles['grid-item-gray']}>
                <ReusableTextField
                  id="emailSignature"
                  label="Email Signature"
                  multiline
                  value={this.state.emailSignature}
                  onChange={(event) => this.handleEmailSignatureChange(event)}
                />
              </div>
            </div>
            <div className={styles['actions-row']}>
              <ReusableButton
                value={this.state.isSaving ? 'Saving' : 'Save'}
                disabled={this.isSaveDisabled()}
                onClick={() => this.saveCurrentValues()}
              />
            </div>
            <ReusableSnackbar
              open={this.props.isError}
              variant="error"
              message="You have unsaved changes."
              optionalButtonMessage="Retry"
              optionalButtonOnClick={() => this.retryUpdate()}
            />
          </div>
        </div>
      </div>
    );
  }
}

EventManagementPage.propTypes = {
  isError: PropTypes.bool.isRequired,
  year: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  eventDates: PropTypes.array.isRequired,
  motd: PropTypes.string.isRequired,
  emailSignature: PropTypes.string.isRequired,
  getYear: PropTypes.func.isRequired,
  updateYear: PropTypes.func.isRequired,
  getEventDates: PropTypes.func.isRequired,
  getEventAdmin: PropTypes.func.isRequired,
  updateEventAdmin: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  yaspFormLink: PropTypes.string.isRequired,
};

export default EventManagementPage;
